import * as React from "react";
import { graphql } from "gatsby";
import styled from "styled-components";
import PageContainer from "../pageContainer";
import { GatsbyImage } from "gatsby-plugin-image";
import { SectionTitle } from "../typography";
import { when } from "../../styles/theme";
import { Button, ButtonRightIcon } from "../button";
import { useLocalization } from "gatsby-theme-i18n";
import { useInView } from "react-intersection-observer";

export const fragment = graphql`
  fragment ProspectusContent on ContentfulPageSectionProspectus {
    name
    title
    description {
      childMarkdownRemark {
        html
      }
    }
    link
    image {
      description
      gatsbyImageData(layout: CONSTRAINED, width: 250, height: 331)
    }

    internal {
      type
    }
  }
`;

const Wrapper = styled.section`
  width: 100%;
  overflow: hidden;
`;

const ProspectusSection = styled(PageContainer)`
  margin-top: 10rem;

  ${when("md")} {
    margin-top: 15rem;
    display: flex;
    align-items: flex-start;
    flex-direction: row-reverse;
    column-gap: ${(props) => props.theme.gutterWidth.md / 2}rem;
  }

  ${when("lg")} {
    column-gap: ${(props) => props.theme.gutterWidth.lg / 2}rem;
  }

  ${when("xl")} {
    column-gap: ${(props) => props.theme.gutterWidth.xl / 2}rem;
  }

  ${when("xxl")} {
    column-gap: ${(props) => props.theme.gutterWidth.xxl / 2}rem;
  }
`;

const DescriptionContainer = styled.div`
  margin-top: 3.4rem;
  color: ${(props) => props.theme.colors.darkBlue};
  max-width: 52rem;
  margin-left: auto;
  margin-right: auto;
`;

const ImageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem;
`;

const ImageContainer = styled.div`
  margin-top: 3rem;
  //width: 100%;

  position: relative;
  background-color: ${(props) => props.theme.colors.darkBlue};
  margin-left: -${(props) => props.theme.pageContainerWidth.xs}rem;
  margin-right: -${(props) => props.theme.pageContainerWidth.xs}rem;

  ${when("sm")} {
    margin-left: -${(props) => props.theme.pageContainerWidth.sm}rem;
    margin-right: -${(props) => props.theme.pageContainerWidth.sm}rem;
  }

  ${when("md")} {
    flex: 1;
    margin-top: 0;
    margin-left: 0;
    margin-right: 0;

    &:after {
      content: "";
      display: block;
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      width: 9999rem;
      background-color: ${(props) => props.theme.colors.darkBlue};
      z-index: -1;
    }
  }
`;

const Title = styled(SectionTitle)`
  ${when("md")} {
    display: none;
  }
`;

const Link = styled(Button)`
  text-align: center;
  margin: 4rem auto 0;

  ${when("md")} {
    margin: 4rem 0 0;
  }
`;

const Left = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const DesktopTitle = styled(SectionTitle)`
  display: none;

  ${when("md")} {
    display: block;
  }
`;

const translations = {
  en: {
    more: "Read now",
  },
  zh: {
    more: "了解更多",
  },
};

const Prospectus = ({ data, setCurrentSection, index }) => {
  const { ref } = useInView({
    onChange: (inView) => {
      if (inView && setCurrentSection) {
        setCurrentSection(index);
      }
    },
    rootMargin: "-30% 0% -30% 0%",
  });

  const { title, description, link, image } = data;

  const { locale } = useLocalization();
  const translation = translations[locale];

  return (
    <Wrapper>
      <ProspectusSection hasSideBar={true} ref={ref} id={`section-${index}`}>
        <Title>{title}</Title>
        <ImageContainer>
          <ImageWrapper>
            <GatsbyImage
              image={image?.gatsbyImageData}
              alt={image?.description}
            />
          </ImageWrapper>
        </ImageContainer>
        <Left>
          <DesktopTitle>{title}</DesktopTitle>
          <DescriptionContainer
            dangerouslySetInnerHTML={{
              __html: description?.childMarkdownRemark.html,
            }}
          ></DescriptionContainer>

          <Link as="a" href={link}>
            {translation["more"]} <ButtonRightIcon />
          </Link>
        </Left>
      </ProspectusSection>
    </Wrapper>
  );
};
export const prospectusName = "ContentfulPageSectionProspectus";

export default Prospectus;
